window.Form = class {
    type = null;
    schema = [];
    store = null;

    constructor(data) {
        this.type = data.type;
        this.schema = data.schema;
    }

    init($store, state) {
        this.store = $store;

        this.schema?.forEach((page, pageIndex) => {
            page.questions?.forEach((question) => {
                const obj = (this.store[question.api_key] = {});

                Object.assign(obj, this.initCommonQuestion(question, page, pageIndex, state));

                if (['options', 'unique_options', 'multiple', 'unique_multiple'].includes(question.type)) {
                    Object.assign(obj, this.initListQuestion(question, page, pageIndex, state));
                }
            });
        });

        return this;
    }

    initCommonQuestion(question, page, pageIndex, state) {
        return {
            value: state[question.api_key] ?? null,
            page: pageIndex + 1,
            warning() {
                return window.applyCustomValidation(question.api_key, this.value || '');
            },
            valid(withMessage = false) {
                if (question.type !== 'info' && !question.is_optional && (this.value === null || this.value === '')) {
                    return false;
                }

                if (question.api_field === 'email' && !validateEmail(this.value)) {
                    return withMessage ? 'Ongeldig e-mailadres.' : false;
                }

                if (question.api_field === 'phoneNumber' && !validatePhoneNumber(this.value)) {
                    return withMessage ? 'Ongeldig telefoonnummer.' : false;
                }

                switch (question.type) {
                    case 'text':
                    case 'textarea':
                        if (question.max_length && this.value?.length > question.max_length) {
                            return withMessage
                                ? 'De tekst mag niet langer zijn dan ' + question.max_length + ' tekens.'
                                : false;
                        }

                        if (question.min_length && this.value?.length < question.min_length) {
                            return withMessage
                                ? 'De tekst moet minimaal ' + question.min_length + ' tekens bevatten.'
                                : false;
                        }

                        return true;

                    case 'number':
                    case 'rating':
                        if (question.min && this.value < question.min) {
                            return withMessage ? 'Het getal moet groter zijn dan ' + question.min + '.' : false;
                        }

                        if (question.max && this.value > question.max) {
                            return withMessage ? 'Het getal moet kleiner zijn dan ' + question.max + '.' : false;
                        }

                        if (
                            question.decimal_places &&
                            this.value?.toString().split('.')[1]?.length > question.decimal_places
                        ) {
                            return withMessage
                                ? 'Het getal mag maximaal ' + question.decimal_places + ' decimalen bevatten.'
                                : false;
                        }

                        return true;

                    case 'date':
                    case 'datetime':
                        return validDateString(this.value);

                    case 'options':
                    case 'unique_options':
                        return getValidMultiSelect(this.options);

                    case 'multiple':
                    case 'unique_multiple':
                        if (this.value === null) {
                            return true;
                        }

                        if (question.min_length && this.value?.length < question.min_length) {
                            return withMessage ? 'Selecteer minimaal ' + question.min_length + ' opties.' : false;
                        }

                        if (question.max_length && this.value?.length > question.max_length) {
                            return withMessage ? 'Selecteer maximaal ' + question.max_length + ' opties.' : false;
                        }

                        for (selected of this.value) {
                            if (!this.options.find((option) => option.key === selected)) {
                                return withMessage ? 'Ongeldige optie geselecteerd.' : false;
                            }
                        }

                        return true;

                    default:
                        return true;
                }
            },
            invalid() {
                return !this.valid();
            },
            getInfoBlockTitle() {
                return question.name;
            },
        };
    }

    initListQuestion(question, page, pageIndex, state) {
        const multi = ['multiple', 'unique_multiple'].includes(question.type);

        return {
            multi,
            options: question.options.map((option) => {
                return {
                    name: option.value,
                    key: option.name,
                    selected: multi
                        ? (state[question.api_key] ?? []).includes(option.name)
                        : state[question.api_key] === option.name,
                    visible: true,
                };
            }),
            canSelectMore() {
                if (!question.max_length) {
                    return true;
                }

                return this.getNumberOfSelectedItems() < question.max_length;
            },
            amountSelected() {
                return this.getNumberOfSelectedItems() + '/' + question.max_length + ' geselecteerd';
            },
            selectOption(option) {
                if (multi) {
                    if (option.selected) {
                        option.selected = !option.selected;
                    } else if (this.getNumberOfSelectedItems() < 3) {
                        option.selected = !option.selected;
                    }

                    this.value = this.selectedOptions();
                } else {
                    this.value = option.key;
                    selectOneValue(option, this.options);
                }
            },
            selectedOptions() {
                return this.options.filter((option) => option.selected).map((option) => option.key);
            },
            valueLabel() {
                return this.options.find((option) => option.key === this.value)?.name;
            },
            filteredOptions() {
                if (this.search === '') {
                    return this.options;
                }

                return this.options.filter((item) => {
                    return item.name.toLowerCase().includes(this.search.toLowerCase());
                });
            },
            getNumberOfSelectedItems() {
                let numberOfSeletedItems = 0;

                for (let counter = 0; counter < this.options.length; counter++) {
                    if (this.options[counter].selected) {
                        numberOfSeletedItems++;
                    }
                }

                return numberOfSeletedItems;
            },
        };
    }

    lastValidQuestion() {
        let lastValidQuestion = null;

        this.schema?.forEach((page) => {
            page.questions?.forEach((question, index) => {
                if (this.store[question.api_key]?.valid()) {
                    lastValidQuestion = index;
                }
            });
        });

        return lastValidQuestion;
    }

    valid(fields = null) {
        let isValid = true;

        this.schema?.forEach((page) => {
            page.questions?.forEach((question) => {
                if (fields === null || fields.includes(question.api_key)) {
                    if (this.store[question.api_key].invalid()) {
                        isValid = false;
                    }
                }
            });
        });

        return isValid;
    }

    firstInvalidInPage(page) {
        let firstInvalid = null;

        this.schema[Math.max(0, page - 1)]?.questions?.forEach((question) => {
            if (this.dependenciesMetForQuestion(question.api_key) && this.store[question.api_key].invalid()) {
                firstInvalid = question.api_key;
            }
        });

        return firstInvalid;
    }

    pageValid(page) {
        return !this.firstInvalidInPage(page);
    }

    getState() {
        const state = {};

        this.schema.forEach((page) => {
            return page.questions.forEach((question) => {
                state[question.api_key] = this.store[question.api_key].value;
            });
        });

        return state;
    }

    skipPage(page) {
        return !this.dependenciesMetForPage(page);
    }

    findQuestion(apiKeyOrId) {
        let question = null;

        for (const page of this.schema) {
            for (const q of page.questions) {
                if (q.id === parseInt(apiKeyOrId) || q.api_key === apiKeyOrId) {
                    question = q;
                    break;
                }
            }

            if (question) {
                break;
            }
        }

        return question;
    }

    dependenciesMetForQuestion(apiKeyOrId, visibility = true, requirement = false) {
        const question = this.findQuestion(apiKeyOrId);

        if (!question) {
            return true;
        }

        return this.dependenciesMet(question.dependencies, visibility, requirement);
    }

    dependenciesMetForPage(pageNumber) {
        const page = this.schema[pageNumber - 1];

        if (!page) {
            return true;
        }

        return this.dependenciesMet(page.dependencies);
    }

    dependenciesMet(dependencies, visibility = true, requirement = false) {
        dependencies = dependencies.filter(
            (dependency) => (!visibility || dependency.becomes_visible) && (!requirement || dependency.becomes_required)
        );

        if (dependencies.length === 0) {
            return true;
        }

        return dependencies.some((dependency) => {
            const dependsOnQuestion = this.findQuestion(dependency.depends_on_question_id);
            if (!dependsOnQuestion) console.log(dependency);
            const answer = this.store[dependsOnQuestion.api_key]?.value || undefined;

            const dependencyValue =
                typeof dependency.data === 'object' && 'value' in dependency.data
                    ? dependency.data.value
                    : dependency.data;

            const result = (() => {
                if (!Array.isArray(answer) && !Array.isArray(dependencyValue)) {
                    switch (dependsOnQuestion.type) {
                        case 'boolean':
                            return answer && !!dependencyValue === !!answer;
                        case 'number':
                        case 'rating':
                            return this.integerDependencyMet(answer, dependencyValue || null);
                        default:
                            return answer && dependencyValue === answer;
                    }
                }

                if (Array.isArray(answer) && Array.isArray(dependencyValue)) {
                    return answer.length > 0 && answer.some((value) => dependencyValue.includes(value));
                }

                if (!Array.isArray(answer) && Array.isArray(dependencyValue)) {
                    return answer && dependencyValue.includes(answer);
                }

                return false;
            })();

            return dependency.reverse ? !result : result;
        });
    }

    integerDependencyMet(answer, dependency) {
        if (answer === null || dependency === null) {
            return false;
        }

        if (dependency.match(/^(\d+)-(\d+)$/)) {
            const [min, max] = dependency.split('-').map((value) => parseInt(value));

            return parseInt(answer) >= min && parseInt(answer) <= max;
        }

        const operator = dependency.replace(/[^<>=]/g, '');
        const dependencyValue = parseInt(dependency.replace(/[^0-9]/g, ''));
        const answerValue = parseInt(answer);

        return (() => {
            switch (operator) {
                case '<':
                    return answerValue < dependencyValue;
                case '>':
                    return answerValue > dependencyValue;
                case '<=':
                    return answerValue <= dependencyValue;
                case '>=':
                    return answerValue >= dependencyValue;
                default:
                    return answerValue === dependencyValue;
            }
        })();
    }
};

window.validDateString = function (value) {
    // Check if the date is valid
    value = value.split('-');
    if (value.length === 3) {
        value = value[1] + '/' + value[0] + '/' + value[2];
        const newDate = new Date(value);
        if (newDate instanceof Date && !isNaN(newDate)) {
            return true;
        }
    }
    return false;
};

window.getValidMultiSelect = function (options) {
    for (let counter = 0; counter < options.length; counter++) {
        if (options[counter].selected) {
            return true;
        }
    }
    return false;
};

window.selectOneValue = function (option, options) {
    for (let counter = 0; counter < options.length; counter++) {
        options[counter].selected = option.name === options[counter].name;
    }
};

window.validateEmail = function (email) {
    const re =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
};

window.validatePhoneNumber = function (number) {
    const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
    return re.test(number);
};
